import type React from 'react';
import type { BreadcrumbItem } from '../ui/Breadcrumbs.js';
import { Card } from '../tailgrids/core/card.js';
import { PageHeader, type PageHelpContent } from '../ui/PageHeader.js';
import { StatStrip, type StatStripCell } from '../ui/StatStrip.js';

interface ListPageProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  helpContent?: PageHelpContent;
  /** The page's create button ("New template") and other page-level actions. */
  actions?: React.ReactNode;
  /** Summary numbers above the list — only when they answer a question the list does not (entity-management §1.2). */
  stats?: StatStripCell[];
  /** Shown above the list instead of it when the list query failed (a `Callout` with Retry). */
  alert?: React.ReactNode;
  /** The list card's header row: `<Toolbar search pills right />`. */
  toolbar?: React.ReactNode;
  /**
   * `table` (default): toolbar and content share one card, the content is a `DataTable` (unframed).
   * `grid`: the toolbar is its own card and the content (a card grid) sits below it on the page background.
   */
  variant?: 'table' | 'grid';
  /** The list itself — or its `EmptyState` / loading placeholder. */
  children: React.ReactNode;
  /** Below the list card: pagination, "showing 20 of 140", bulk actions. */
  footer?: React.ReactNode;
}

/**
 * The list page template. Template example: NextAdmin `(dashboard)/(home)/_component/last-transactions-table`
 * (`Card` p-5, `CardHeader className="mb-6"`, `TableRoot className="rounded-none border-none"`) and demo
 * `/tables/data-tables`: header with the create action, optional summary numbers, then one card holding the toolbar
 * and the table. Every entity list uses it — never assemble a list page by hand.
 */
export function ListPage({
  title,
  subtitle,
  breadcrumbs,
  helpContent,
  actions,
  stats,
  alert,
  toolbar,
  variant = 'table',
  children,
  footer,
}: ListPageProps) {
  return (
    <div className="space-y-5">
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        helpContent={helpContent}
        actions={actions}
      />
      {stats && stats.length > 0 && <StatStrip cells={stats} className="mb-5" />}
      {alert}
      {variant === 'table' ? (
        <Card>
          {toolbar}
          {children}
        </Card>
      ) : (
        <>
          {toolbar && <Card className="[&>*]:mb-0">{toolbar}</Card>}
          {children}
        </>
      )}
      {footer}
    </div>
  );
}
